import { defineRecipe, type SystemStyleObject } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

// `as const` is load-bearing: without it `(typeof buttonVariants)[number]` widens to `string`,
// which collapses the recipe's `variant` prop type to `string` — losing TS autocomplete/checking
// and leaving Storybook/docgen (and the bitkit-storybook MCP) unable to enumerate the variants.
const buttonVariants = [
  'primary',
  'secondary',
  'tertiary',
  'danger-primary',
  'danger-secondary',
  'danger-tertiary',
  'ai-primary',
  'ai-secondary',
  'ai-tertiary',
] as const;

export type Variant = (typeof buttonVariants)[number];

const variantStyles = buttonVariants.reduce(
  (obj, variant) => {
    let borderColor = variant?.includes('secondary') ? `button/${variant}/border` : `button/${variant}/bg`;
    let disabledBorderColor = variant?.includes('secondary')
      ? `button/${variant}/border-disabled`
      : `button/${variant}/bg-disabled`;
    if (variant?.includes('tertiary')) {
      borderColor = 'transparent';
      disabledBorderColor = 'transparent';
    }
    const isSecondary = variant === 'secondary';
    const activeStyles: SystemStyleObject = {
      backgroundColor: `button/${variant}/bg-active`,
      borderColor: variant?.includes('secondary') ? `button/${variant}/border-active` : `button/${variant}/bg-active`,
      color: isSecondary ? 'button/secondary/text-active' : `button/${variant}/fg-active`,
      _icon: {
        color: isSecondary ? 'button/secondary/icon-active' : undefined,
      },
    };
    obj[variant as NonNullable<Variant>] = {
      // `_hover`/`_active` (not raw `&:hover`/`&:active`) compile to `:not(:disabled, [data-disabled])`,
      // so they never match a disabled button — the `_disabled` styles win naturally, no `!important` needed.
      _hover: {
        backgroundColor: `button/${variant}/bg-hover`,
        borderColor: variant?.includes('secondary') ? `button/${variant}/border-hover` : `button/${variant}/bg-hover`,
        color: isSecondary ? 'button/secondary/text-hover' : `button/${variant}/fg-hover`,
        _icon: {
          color: isSecondary ? 'button/secondary/icon-hover' : undefined,
        },
        // Repeat active under hover so clicking-while-hovering keeps active styles (see CLAUDE.md).
        _active: activeStyles,
      },
      _active: activeStyles,
      _disabled: {
        backgroundColor: `button/${variant}/bg-disabled`,
        borderColor: disabledBorderColor,
        color: isSecondary ? 'button/secondary/text-disabled' : `button/${variant}/fg-disabled`,
        cursor: 'not-allowed',
        _icon: {
          color: isSecondary ? 'button/secondary/icon-disabled' : undefined,
        },
      },
      _icon: {
        color: isSecondary ? 'button/secondary/icon' : undefined,
      },
      backgroundColor: `button/${variant}/bg`,
      borderColor,
      borderStyle: 'solid',
      borderWidth: '1',
      color: isSecondary ? 'button/secondary/text' : `button/${variant}/fg`,
    };
    return obj;
  },
  {} as Record<NonNullable<Variant>, SystemStyleObject>,
);

const buttonRecipe = defineRecipe({
  className: 'button',
  base: {
    borderRadius: '4',
    cursor: 'pointer',
    fontWeight: 600,
    border: '1px solid transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8',
    position: 'relative',
    '--spinner-color': 'currentColor',
    '--spinner-size': 'sizes.16',
    _disabled: {
      cursor: 'not-allowed',
    },
  },
  variants: {
    variant: variantStyles,
    size: {
      sm: {
        minWidth: '32',
        height: '32',
        paddingInline: rem(11),
        paddingBlock: rem(5),
        textStyle: 'comp/button/sm',
      },
      md: {
        minWidth: '40',
        height: '40',
        paddingInline: rem(15),
        paddingBlock: rem(7),
        textStyle: 'comp/button/md',
      },
      lg: {
        minWidth: '48',
        height: '48',
        padding: rem(15),
        textStyle: 'comp/button/lg',
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'lg',
  },
});

export default buttonRecipe;
